import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  useTheme,
} from '@mui/material';
import { tokens } from '../../../theme';

import { useGetReleaseDataAssetsQuery } from '../../../features/data_assets/dataAssetsApiSlice';
import { useGetOrganizationsQuery } from '../../../features/organizations/organizationsApiSlice';
import {
  useGetReleasesQuery,
  useCreateReleaseMutation,
  useUpdateReleaseMutation,
  useCancelReleaseMutation,
  useDeleteReleaseMutation,
  useFinalizeReleaseTrackMutation,
  useUnfinalizeReleaseTrackMutation,
  useGetReleaseOrgImpactQuery,
  useGetReleaseDeliveriesQuery,
  useUpdateSubReleaseDateMutation,
  useUpdateReleaseTargetMutation,
} from '../../../features/releases/releasesApiSlice';

import CreateReleaseDialog from './CreateReleaseDialog';
import {
  ReleaseHeader,
  ReleaseProductsPanel,
  OrgImpactPanel,
  DeliveriesPanel,
  ConfirmAllDialog,
  RecipientOrgsDialog,
  buildProductsByReleaseType,
  buildRecipientDialogRows,
  buildReadiness,
  getVersionMetaFromAssetMap,
} from './release';

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box pt={2}>{children}</Box>;
}

const ReleaseDeliveryTab = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState('');
  const [releaseTab, setReleaseTab] = useState(0);
  const [selectedReleaseId, setSelectedReleaseId] = useState(null);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogReleaseType, setConfirmDialogReleaseType] = useState(null);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [confirmProgress, setConfirmProgress] = useState(0);

  const [finalizeErrors, setFinalizeErrors] = useState({
    Contact: null,
    Digital: null,
  });

  const [recipientDialogOpen, setRecipientDialogOpen] = useState(false);
  const [recipientDialogReleaseType, setRecipientDialogReleaseType] = useState(null);
  const [recipientDialogProduct, setRecipientDialogProduct] = useState(null);
  const [setAllToLatestStatus, setSetAllToLatestStatus] = useState({
    Contact: { loading: false, error: '', success: false },
    Digital: { loading: false, error: '', success: false },
  });

  const [finalizingReleaseType, setFinalizingReleaseType] = useState(null);

const { data: dataAssetsNorm, isLoading: loadingAssets } = useGetReleaseDataAssetsQuery(undefined, {
  pollingInterval: 15000,
  refetchOnFocus: true,
  refetchOnMountOrArgChange: true,
});

  const { data: orgsNorm, isLoading: loadingOrgs } = useGetOrganizationsQuery('organizations', {
    pollingInterval: 15000,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });

  const { data: releasesNorm, isLoading: loadingReleases, refetch: refetchReleases } =
    useGetReleasesQuery(undefined, {
      pollingInterval: 15000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    });

  const [createRelease, { isLoading: creating }] = useCreateReleaseMutation();
  const [updateRelease, { isLoading: updating }] = useUpdateReleaseMutation();
  const [updateSubReleaseDate, { isLoading: updatingSubReleaseDate }] = useUpdateSubReleaseDateMutation();
  const [cancelRelease, { isLoading: canceling }] = useCancelReleaseMutation();
  const [deleteRelease, { isLoading: deleting }] = useDeleteReleaseMutation();
  const [finalizeReleaseTrack, { isLoading: finalizingTrack }] = useFinalizeReleaseTrackMutation();
  const [unfinalizeReleaseTrack, { isLoading: unfinalizingTrack }] = useUnfinalizeReleaseTrackMutation();
  const [updateReleaseTarget, { isLoading: updatingReleaseTarget }] = useUpdateReleaseTargetMutation();

  const allDataAssets = useMemo(() => Object.values(dataAssetsNorm?.entities || {}), [dataAssetsNorm]);
  const allOrgs = useMemo(() => Object.values(orgsNorm?.entities || {}), [orgsNorm]);
  const allReleases = useMemo(() => Object.values(releasesNorm?.entities || {}), [releasesNorm]);

  const productsByReleaseType = useMemo(
    () => buildProductsByReleaseType(allDataAssets),
    [allDataAssets]
  );

  const [finalizeDeliveryActions, setFinalizeDeliveryActions] = useState({
    Contact: {},
    Digital: {},
  });

  const sortedReleases = useMemo(() => {
    const list = [...allReleases];
    list.sort((a, b) => {
      const aDates = [
        a?.contact?.scheduled_release_date_utc,
        a?.digital?.scheduled_release_date_utc,
        a?.createdAt,
        a?.created_at,
      ].filter(Boolean);
      const bDates = [
        b?.contact?.scheduled_release_date_utc,
        b?.digital?.scheduled_release_date_utc,
        b?.createdAt,
        b?.created_at,
      ].filter(Boolean);

      const da = aDates.length ? Math.max(...aDates.map((d) => new Date(d).getTime())) : 0;
      const db = bDates.length ? Math.max(...bDates.map((d) => new Date(d).getTime())) : 0;

      return db - da;
    });
    return list;
  }, [allReleases]);

  const selectedRelease = useMemo(() => {
    const fallback = sortedReleases[0] || null;
    return (selectedReleaseId && (releasesNorm?.entities?.[selectedReleaseId] || null)) || fallback;
  }, [selectedReleaseId, sortedReleases, releasesNorm]);

  const selectedReleaseDbId = selectedRelease?._id || selectedRelease?.id || null;

  const {
    data: contactOrgImpactData,
    isLoading: loadingContactOrgImpact,
    isFetching: fetchingContactOrgImpact,
    refetch: refetchContactOrgImpact,
  } = useGetReleaseOrgImpactQuery(
    { id: selectedReleaseDbId, releaseType: 'Contact' },
    {
      skip: !selectedReleaseDbId || !selectedRelease?.contact?.enabled,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: digitalOrgImpactData,
    isLoading: loadingDigitalOrgImpact,
    isFetching: fetchingDigitalOrgImpact,
    refetch: refetchDigitalOrgImpact,
  } = useGetReleaseOrgImpactQuery(
    { id: selectedReleaseDbId, releaseType: 'Digital' },
    {
      skip: !selectedReleaseDbId || !selectedRelease?.digital?.enabled,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: releaseDeliveriesData,
    isLoading: loadingReleaseDeliveries,
    isFetching: fetchingReleaseDeliveries,
    refetch: refetchReleaseDeliveries,
  } = useGetReleaseDeliveriesQuery(
    { id: selectedReleaseDbId },
    {
      skip: !selectedReleaseDbId,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  const handleSetFinalizeDeliveryAction = (releaseType, deliveryActionKey, action) => {
    setFinalizeDeliveryActions((prev) => ({
      ...prev,
      [releaseType]: {
        ...(prev[releaseType] || {}),
        [deliveryActionKey]: {
          delivery_action_key: deliveryActionKey,
          ...action,
        },
      },
    }));
  };

  const selectedReleaseStatus = selectedRelease?.status || '';
  const releaseLocked = ['scheduled', 'releasing', 'released', 'canceled'].includes(selectedReleaseStatus);
  const canCancelRelease = ['draft', 'scheduled'].includes(selectedReleaseStatus);
  const canDeleteRelease = selectedReleaseStatus === 'canceled';

  const assetMap = useMemo(() => {
    const map = new Map();
    for (const relType of ['Contact', 'Digital']) {
      for (const p of productsByReleaseType?.[relType] || []) {
        map.set(String(p.dataAssetId), p);
      }
    }
    return map;
  }, [productsByReleaseType]);

  const readiness = useMemo(() => buildReadiness(selectedRelease), [selectedRelease]);

  const orgImpactRows = useMemo(() => {
    const contactRows = Array.isArray(contactOrgImpactData?.rows) ? contactOrgImpactData.rows : [];
    const digitalRows = Array.isArray(digitalOrgImpactData?.rows) ? digitalOrgImpactData.rows : [];
    return [...contactRows, ...digitalRows];
  }, [contactOrgImpactData, digitalOrgImpactData]);

  const recipientDialogRows = useMemo(
    () =>
      buildRecipientDialogRows({
        recipientDialogOpen,
        recipientDialogProduct,
        recipientDialogReleaseType,
        allOrgs,
        getVersionMeta: getVersionMetaFromAssetMap,
        assetMap,
      }),
    [recipientDialogOpen, recipientDialogProduct, recipientDialogReleaseType, allOrgs, assetMap]
  );

  const releaseDeliveryRows = useMemo(() => {
    return Array.isArray(releaseDeliveriesData?.rows) ? releaseDeliveriesData.rows : [];
  }, [releaseDeliveriesData]);

  const isBusy =
    loadingAssets ||
    loadingOrgs ||
    loadingReleases ||
    creating ||
    updating ||
    updatingReleaseTarget ||
    canceling ||
    deleting ||
    finalizingTrack ||
    unfinalizingTrack ||
    loadingContactOrgImpact ||
    loadingDigitalOrgImpact ||
    fetchingContactOrgImpact ||
    fetchingDigitalOrgImpact ||
    loadingReleaseDeliveries ||
    fetchingReleaseDeliveries;

  const noReleases = !loadingReleases && sortedReleases.length === 0;

  const patchEntireRelease = async (next, releaseType = null) => {
    if (!selectedRelease?.id) return;

    if (releaseType && isTrackLocked(selectedRelease, releaseType)) return;

    await updateRelease({ id: selectedRelease.id, ...next }).unwrap();
    await refetchReleases();

    if (selectedRelease?.contact?.enabled) await refetchContactOrgImpact();
    if (selectedRelease?.digital?.enabled) await refetchDigitalOrgImpact();
  };

  const isTrackLocked = (release, releaseType) => {
    const key = releaseType === 'Contact' ? 'contact' : 'digital';
    const status = release?.[key]?.status || 'draft';
    return ['scheduled', 'releasing', 'released', 'canceled'].includes(status);
  };

  const patchReleaseItem = async ({ releaseType, dataAssetId, patch }) => {
    if (!selectedRelease?.id || isTrackLocked(selectedRelease, releaseType)) return;

    const next = structuredClone(selectedRelease);
    const key = releaseType === 'Contact' ? 'contact' : 'digital';
    next[key] = next[key] || { release: releaseType, items: [] };
    next[key].items = Array.isArray(next[key].items) ? next[key].items : [];

    const idx = next[key].items.findIndex((it) => String(it.data_asset_id) === String(dataAssetId));
    if (idx === -1) return;

    next[key].items[idx] = { ...next[key].items[idx], ...patch };

    await patchEntireRelease(next, releaseType);
  };

  const setAllToLatest = async (releaseType) => {
    if (!selectedRelease?.id || isTrackLocked(selectedRelease, releaseType)) return;

    setSetAllToLatestStatus((prev) => ({
      ...prev,
      [releaseType]: { loading: true, error: '', success: false },
    }));

    try {
      const key = releaseType === 'Contact' ? 'contact' : 'digital';
      const next = structuredClone(selectedRelease);
      const items = Array.isArray(next?.[key]?.items) ? next[key].items : [];

      let changedCount = 0;
      let missingLatestCount = 0;

      next[key].items = items.map((it) => {
        const product = assetMap.get(String(it.data_asset_id));
        const versions = Array.isArray(product?.versions) ? product.versions : [];
        const latest = versions.length ? versions[versions.length - 1] : null;

        if (!latest?.version) {
          missingLatestCount += 1;
          return it;
        }

        if (it.version !== latest.version) changedCount += 1;

        return {
          ...it,
          version: latest.version,
          version_timestamp_utc: latest.timestamp_utc || undefined,
          confirmed: false,
        };
      });

      await patchEntireRelease(next, releaseType);

      setSetAllToLatestStatus((prev) => ({
        ...prev,
        [releaseType]: {
          loading: false,
          error: '',
          success: true,
          changedCount,
          missingLatestCount,
        },
      }));

      setTimeout(() => {
        setSetAllToLatestStatus((prev) => ({
          ...prev,
          [releaseType]: { loading: false, error: '', success: false },
        }));
      }, 5000);
    } catch (err) {
      setSetAllToLatestStatus((prev) => ({
        ...prev,
        [releaseType]: {
          loading: false,
          error: err?.data?.message || err?.message || 'Unknown error',
          success: false,
        },
      }));
    }
  };

  const getPendingConfirmItems = (releaseType) => {
    const key = releaseType === 'Contact' ? 'contact' : 'digital';
    const items = Array.isArray(selectedRelease?.[key]?.items) ? selectedRelease[key].items : [];

    return items
      .filter((it) => it?.version && !it?.confirmed)
      .map((it) => {
        const meta = getVersionMetaFromAssetMap(assetMap, releaseType, it.data_asset_id, it.version);
        const timestamp = meta?.timestamp_utc || it?.version_timestamp_utc;
        const ms = timestamp ? new Date().getTime() - new Date(timestamp).getTime() : null;
        const daysOld = ms == null ? null : Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));

        return {
          ...it,
          version_timestamp_utc: timestamp,
          daysOld,
        };
      });
  };

  const confirmSummary = useMemo(() => {
    if (!confirmDialogReleaseType) {
      return { items: [], staleItems: [], freshItems: [], total: 0, hasStale: false };
    }

    const items = getPendingConfirmItems(confirmDialogReleaseType);
    const staleItems = items.filter((it) => (it.daysOld ?? 9999) > 30);
    const freshItems = items.filter((it) => (it.daysOld ?? 9999) <= 30);

    return {
      items,
      staleItems,
      freshItems,
      total: items.length,
      hasStale: staleItems.length > 0,
    };
  }, [confirmDialogReleaseType, selectedRelease, assetMap]);

  const confirmAllSelected = async (releaseType) => {
    if (!selectedRelease?.id || isTrackLocked(selectedRelease, releaseType)) return;

    const key = releaseType === 'Contact' ? 'contact' : 'digital';
    const next = structuredClone(selectedRelease);
    const items = Array.isArray(next?.[key]?.items) ? next[key].items : [];

    const pendingIndexes = [];
    items.forEach((it, idx) => {
      if (it?.version && !it?.confirmed) pendingIndexes.push(idx);
    });

    if (!pendingIndexes.length) return;

    setConfirmingAll(true);
    setConfirmProgress(0);

    try {
      const total = pendingIndexes.length;

      for (let i = 0; i < pendingIndexes.length; i += 1) {
        const idx = pendingIndexes[i];
        next[key].items[idx] = {
          ...next[key].items[idx],
          confirmed: true,
        };
        setConfirmProgress(Math.round(((i + 1) / total) * 100));
      }

      await patchEntireRelease(next, releaseType);
      setConfirmDialogOpen(false);
      setConfirmDialogReleaseType(null);
    } finally {
      setConfirmingAll(false);
      setConfirmProgress(0);
    }
  };

  const handleFinalizeTrack = async (releaseType) => {
    if (!selectedRelease?.id || isTrackLocked(selectedRelease, releaseType)) return;

    setFinalizeErrors((prev) => ({
      ...prev,
      [releaseType]: null,
    }));

    setFinalizingReleaseType(releaseType);

    try {
      await finalizeReleaseTrack({
        id: selectedRelease.id,
        releaseType,
        deliveryActions: finalizeDeliveryActions[releaseType] || {},
      }).unwrap();

      setFinalizeDeliveryActions((prev) => ({
        ...prev,
        [releaseType]: {},
      }));

      await refetchReleases();
      await refetchReleaseDeliveries();

      if (releaseType === 'Contact') await refetchContactOrgImpact();
      if (releaseType === 'Digital') await refetchDigitalOrgImpact();
    } catch (err) {
      const data = err?.data || {};

      setFinalizeErrors((prev) => ({
        ...prev,
        [releaseType]: {
          message:
            data?.message ||
            err?.message ||
            `Failed to finalize ${releaseType} release.`,
          code: data?.code,
          count: data?.count,
          sample: Array.isArray(data?.sample) ? data.sample : [],
          invalidActions: Array.isArray(data?.invalidActions) ? data.invalidActions : [],
        },
      }));
    } finally {
      setFinalizingReleaseType(null);
    }
  };

  const handleUnfinalizeTrack = async (releaseType) => {
    if (!selectedRelease?.id) return;

    await unfinalizeReleaseTrack({
      id: selectedRelease.id,
      releaseType,
    }).unwrap();

    await refetchReleases();

    if (selectedRelease?.contact?.enabled) {
      await refetchContactOrgImpact();
    }

    if (selectedRelease?.digital?.enabled) {
      await refetchDigitalOrgImpact();
    }

    await refetchReleaseDeliveries();
  };

  const handleOpenCreate = () => {
    setCreateError('');
    setCreateOpen(true);
  };

  const handleCloseCreate = () => {
    if (creating) return;
    setCreateError('');
    setCreateOpen(false);
  };

  const handleUpdateSubReleaseDate = async (releaseType, scheduledIso) => {
    if (!selectedRelease?.id || !scheduledIso) return;

    await updateSubReleaseDate({
      id: selectedRelease.id,
      releaseType,
      scheduled_release_date_utc: scheduledIso,
    }).unwrap();

    await refetchReleases();

    if (releaseType === 'Contact') await refetchContactOrgImpact();
    if (releaseType === 'Digital') await refetchDigitalOrgImpact();
  };

  const handleUpdateReleaseTarget = async (target) => {
    if (!selectedReleaseDbId || releaseLocked) return;

    await updateReleaseTarget({
      id: selectedReleaseDbId,
      target,
    }).unwrap();

    await refetchReleases();

    if (selectedRelease?.contact?.enabled) {
      await refetchContactOrgImpact();
    }

    if (selectedRelease?.digital?.enabled) {
      await refetchDigitalOrgImpact();
    }
  };

  const handleCreate = async (payload) => {
    setCreateError('');

    try {
      const created = await createRelease(payload).unwrap();

      const newId = created?.id || created?._id;
      if (newId) {
        setSelectedReleaseId(String(newId));
      }

      setReleaseTab(0);
      await refetchReleases();
      setCreateOpen(false);
    } catch (err) {
      const message =
        err?.data?.message ||
        err?.error ||
        err?.message ||
        'Failed to create release. Please review the form and try again.';

      setCreateError(message);
    }
  };

  const handleCancelRelease = async () => {
    if (!selectedRelease?.id || !canCancelRelease) return;
    await cancelRelease({ id: selectedRelease.id }).unwrap();
    await refetchReleases();
  };

  const handleDeleteRelease = async () => {
    if (!selectedRelease?.id || !canDeleteRelease) return;
    await deleteRelease({ id: selectedRelease.id }).unwrap();
    setSelectedReleaseId(null);
    setReleaseTab(0);
    await refetchReleases();
  };

  return (
    <Box>
      <ReleaseHeader
        colors={colors}
        noReleases={noReleases}
        selectedRelease={selectedRelease}
        readiness={readiness}
        isBusy={isBusy}
        sortedReleases={sortedReleases}
        selectedReleaseId={selectedRelease?.id || ''}
        setSelectedReleaseId={setSelectedReleaseId}
        canCancelRelease={canCancelRelease}
        canDeleteRelease={canDeleteRelease}
        handleCancelRelease={handleCancelRelease}
        handleDeleteRelease={handleDeleteRelease}
        setCreateOpen={handleOpenCreate}
        allOrgs={allOrgs}
        onUpdateReleaseTarget={handleUpdateReleaseTarget}
        updatingReleaseTarget={updatingReleaseTarget}
      />

      {noReleases ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 1, background: colors.primary[500] }}>
          <Button
            variant="contained"
            disabled={isBusy}
            onClick={handleOpenCreate}
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[100],
              '&:hover': { backgroundColor: colors.greenAccent[400] },
            }}
          >
            Create your first release
          </Button>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
          <Tabs
            value={releaseTab}
            onChange={(e, v) => setReleaseTab(v)}
            textColor="inherit"
            sx={{
              '& .MuiTab-root': { color: colors.secondary[100] },
              '& .Mui-selected': { color: colors.greenAccent[300] },
              '& .MuiTabs-indicator': { backgroundColor: colors.greenAccent[300] },
            }}
          >
            <Tab label="Contact" />
            <Tab label="Digital" />
            <Tab label="Org impact" />
            <Tab label="Deliveries" />
          </Tabs>

          <Divider sx={{ mt: 1 }} />

          <TabPanel value={releaseTab} index={0}>
            <ReleaseProductsPanel
              releaseType="Contact"
              colors={colors}
              selectedRelease={selectedRelease}
              releaseLocked={releaseLocked}
              productsByReleaseType={productsByReleaseType}
              assetMap={assetMap}
              patchReleaseItem={patchReleaseItem}
              setAllToLatest={setAllToLatest}
              setAllToLatestStatus={setAllToLatestStatus}
              finalizingReleaseType={finalizingReleaseType}
              setConfirmDialogReleaseType={setConfirmDialogReleaseType}
              setConfirmDialogOpen={setConfirmDialogOpen}
              setRecipientDialogReleaseType={setRecipientDialogReleaseType}
              setRecipientDialogProduct={setRecipientDialogProduct}
              setRecipientDialogOpen={setRecipientDialogOpen}
              onFinalizeTrack={handleFinalizeTrack}
              onUnfinalizeTrack={handleUnfinalizeTrack}
              onUpdateScheduledDate={handleUpdateSubReleaseDate}
              updatingSubReleaseDate={updatingSubReleaseDate}
              finalizeError={finalizeErrors.Contact}
              onClearFinalizeError={() =>
                setFinalizeErrors((prev) => ({ ...prev, Contact: null }))
              }
              finalizeDeliveryActions={finalizeDeliveryActions.Contact || {}}
              onSetFinalizeDeliveryAction={(deliveryActionKey, action) =>
                handleSetFinalizeDeliveryAction('Contact', deliveryActionKey, action)
              }
            />
          </TabPanel>

          <TabPanel value={releaseTab} index={1}>
            <ReleaseProductsPanel
              releaseType="Digital"
              colors={colors}
              selectedRelease={selectedRelease}
              releaseLocked={releaseLocked}
              productsByReleaseType={productsByReleaseType}
              assetMap={assetMap}
              patchReleaseItem={patchReleaseItem}
              setAllToLatest={setAllToLatest}
              setAllToLatestStatus={setAllToLatestStatus}
              finalizingReleaseType={finalizingReleaseType}
              setConfirmDialogReleaseType={setConfirmDialogReleaseType}
              setConfirmDialogOpen={setConfirmDialogOpen}
              setRecipientDialogReleaseType={setRecipientDialogReleaseType}
              setRecipientDialogProduct={setRecipientDialogProduct}
              setRecipientDialogOpen={setRecipientDialogOpen}
              onFinalizeTrack={handleFinalizeTrack}
              onUnfinalizeTrack={handleUnfinalizeTrack}
              onUpdateScheduledDate={handleUpdateSubReleaseDate}
              updatingSubReleaseDate={updatingSubReleaseDate}
              finalizeError={finalizeErrors.Digital}
              onClearFinalizeError={() =>
                setFinalizeErrors((prev) => ({ ...prev, Digital: null }))
              }
              finalizeDeliveryActions={finalizeDeliveryActions.Digital || {}}
              onSetFinalizeDeliveryAction={(deliveryActionKey, action) =>
                handleSetFinalizeDeliveryAction('Digital', deliveryActionKey, action)
              }
            />
          </TabPanel>

          <TabPanel value={releaseTab} index={2}>
            <OrgImpactPanel
              colors={colors}
              rows={orgImpactRows}
              isLoading={
                loadingContactOrgImpact ||
                loadingDigitalOrgImpact ||
                fetchingContactOrgImpact ||
                fetchingDigitalOrgImpact
              }
            />
          </TabPanel>

          <TabPanel value={releaseTab} index={3}>
            <DeliveriesPanel
              colors={colors}
              rows={releaseDeliveryRows}
              isLoading={loadingReleaseDeliveries || fetchingReleaseDeliveries}
            />
          </TabPanel>

          <Divider sx={{ mt: 2, mb: 2 }} />
        </Paper>
      )}

      <CreateReleaseDialog
        open={createOpen}
        onClose={handleCloseCreate}
        productsByRelease={productsByReleaseType}
        onCreate={handleCreate}
        isSubmitting={creating}
        submitError={createError}
      />

      <ConfirmAllDialog
        open={confirmDialogOpen}
        onClose={() => (!confirmingAll ? setConfirmDialogOpen(false) : null)}
        colors={colors}
        confirmDialogReleaseType={confirmDialogReleaseType}
        confirmSummary={confirmSummary}
        confirmingAll={confirmingAll}
        confirmProgress={confirmProgress}
        onConfirm={() => confirmAllSelected(confirmDialogReleaseType)}
      />

      <RecipientOrgsDialog
        open={recipientDialogOpen}
        onClose={() => setRecipientDialogOpen(false)}
        colors={colors}
        recipientDialogProduct={recipientDialogProduct}
        recipientDialogReleaseType={recipientDialogReleaseType}
        recipientDialogRows={recipientDialogRows}
      />
    </Box>
  );
};

export default ReleaseDeliveryTab;